import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// --- SVG ICONS ---
const VerifiedUserIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline>
    </svg>
);

const StarIcon = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
  </svg>
);

const baseURL = process.env.REACT_APP_BACKEND_URL

const AccountPage = () => {
    const navigate = useNavigate();
    const userId = localStorage.getItem('user_id') || 'anonymous-user';
    const userName = localStorage.getItem('name') || 'Anonymous User';
    const userEmail = localStorage.getItem('email') || 'anonymous@example.com';
    const token = localStorage.getItem('token');

    const user = {
        name: (userId === 'anonymous-user' || userId === 'anonymous-user-failed') ? 'Anonymous User' : userName,
        email: (userId === 'anonymous-user' || userId === 'anonymous-user-failed') ? 'anonymous@example.com' : userEmail,
        joinDate: '2024-01-15', // This could be dynamic in a real app
        user_id: userId
    };

    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const hasFetched = useRef(false);
    
    // --- FADE-IN ANIMATION LOGIC ---
    const pageRef = useRef(null);
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('visible');
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.1 }
        );

        if (pageRef.current) {
            observer.observe(pageRef.current);
        }

        return () => {
            if (pageRef.current) {
                observer.unobserve(pageRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (hasFetched.current || userId === 'anonymous-user' || !token) {
            setLoading(false);
            setReviews([]);
            return;
        }

        hasFetched.current = true;

        const fetchReviews = async () => {

            try {
                const res = await fetch(baseURL + '/api/review/get-by-user-id', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ user_id: userId })
                });

                const data = await res.json();
                if (!res.ok) {
                    throw new Error(data.message || 'Failed to fetch reviews');
                }
                setReviews(data.reviews || []);
            } catch (err) {
                setError(err.message || 'Error fetching reviews');
            } finally {
                setLoading(false);
            }
        };

        fetchReviews();
    }, [token, userId]);

    return (
        <>
            {/* --- CSS STYLES --- */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

                .account-page {
                    font-family: 'Inter', sans-serif;
                    background-color: #f9fafb;
                    padding: 4rem 1.5rem;
                    min-height: 100vh;
                }
                .container {
                    max-width: 1200px;
                    margin: 0 auto;
                }
                .page-title {
                    font-size: 2.5rem;
                    font-weight: 700;
                    margin-bottom: 2.5rem;
                    color: #111827;
                }
                .account-grid {
                    display: grid;
                    grid-template-columns: 1fr 2fr;
                    gap: 2.5rem;
                }
                
                /* Fade-in Animation */
                .fade-in-section {
                    opacity: 0;
                    transform: translateY(30px);
                    transition: opacity 0.8s ease-out, transform 0.8s ease-out;
                }
                .fade-in-section.visible {
                    opacity: 1;
                    transform: translateY(0);
                }

                /* User Card */
                .user-card {
                    text-align: center;
                    padding: 2.5rem;
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06);
                }
                .user-avatar {
                    width: 90px;
                    height: 90px;
                    border-radius: 50%;
                    background-color: #4f46e5;
                    color: white;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 2.5rem;
                    font-weight: 600;
                    margin: 0 auto 1.5rem auto;
                }
                .user-name {
                    font-size: 1.75rem;
                    font-weight: 700;
                }
                .user-email {
                    color: #6b7280;
                    margin-bottom: 1rem;
                }
                .verified-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    background-color: #dcfce7;
                    color: #166534;
                    padding: 0.3rem 1rem;
                    border-radius: 999px;
                    font-weight: 500;
                    font-size: 0.9rem;
                    margin-bottom: 1.5rem;
                }
                .join-date {
                    color: #6b7280;
                    font-size: 0.9rem;
                }
                
                /* Contributions Card */
                .contributions-card {
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06);
                    padding: 2.5rem;
                }
                .contributions-title {
                    font-size: 1.5rem;
                    font-weight: 600;
                    padding-bottom: 1rem;
                    margin-bottom: 1.5rem;
                    border-bottom: 1px solid #e5e7eb;
                }
                .review-item {
                    margin-bottom: 2rem;
                    padding-bottom: 2rem;
                    border-bottom: 1px solid #f3f4f6;
                }
                .review-item:last-child {
                    margin-bottom: 0;
                    padding-bottom: 0;
                    border-bottom: none;
                }
                .review-address {
                    font-weight: 600;
                    font-size: 1.1rem;
                    margin-bottom: 0.5rem;
                }
                .review-text {
                    color: #4b5563;
                    font-style: italic;
                }
                .review-rating {
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                    margin-top: 0.75rem;
                }
                .star-filled { color: #f59e0b; }
                
                /* States: Loading, Error, Empty */
                .state-container { text-align: center; padding: 3rem 0; }
                .loader {
                    border: 4px solid #e5e7eb;
                    border-radius: 50%;
                    border-top-color: #4f46e5;
                    width: 40px;
                    height: 40px;
                    animation: spin 1s ease-in-out infinite;
                    margin: 0 auto 1rem auto;
                }
                @keyframes spin { to { transform: rotate(360deg); } }
                .error-message { color: #b91c1c; }
                
                .styled-button {
                    background-color: #2563eb;
                    color: white;
                    font-weight: 600;
                    padding: 0.7rem 1.5rem;
                    border: none;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: background-color 0.3s, transform 0.2s;
                    display: inline-flex;
                    margin-top: 1rem;
                }
                .styled-button:hover {
                    background-color: #1d4ed8;
                    transform: translateY(-2px);
                }

                /* Responsive */
                @media (max-width: 900px) {
                    .account-grid {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>
            
            {/* --- JSX STRUCTURE --- */}
            <div className="account-page">
                <div className="container fade-in-section" ref={pageRef}>
                    <h1 className="page-title">My Account</h1>
                    <div className="account-grid">
                        <div className="user-card">
                            <div className="user-avatar">{user.name.split(' ').map(n => n[0]).join('')}</div>
                            <h2 className="user-name">{user.name}</h2>
                            <p className="user-email">{user.email}</p>
                            <div className="verified-badge">
                                <VerifiedUserIcon />
                                <span>{(userId === 'anonymous-user' || userId === 'anonymous-user-failed') ? 'Anonymous Session' : 'Verified Member'}</span>
                            </div>
                        </div>
                        <div className="contributions-card">
                            <h3 className="contributions-title">My Contributions</h3>
                            {loading ? (
                                <div className="state-container">
                                    <div className="loader"></div>
                                    <p>Loading your contributions...</p>
                                </div>
                            ) : error ? (
                                <div className="state-container">
                                    <p className="error-message">{error}</p>
                                </div>
                            ) : reviews.length === 0 ? (
                                <div className="state-container">
                                    <p>You have not made any contributions yet.</p>
                                    <button className="styled-button" onClick={() => navigate('/submit')}>
                                        Submit a Review
                                    </button>
                                </div>
                            ) : (
                                reviews.map((review, idx) => (
                                    <div className="review-item" key={idx}>
                                        <p className="review-address">{review.address || 'Unknown Property'}</p>
                                        <p className="review-text">"{review.review_text || 'No comment provided.'}"</p>
                                        <div className="review-rating">
                                            {[...Array(review.stars || 0)].map((_, j) => <StarIcon key={j} className="star-filled" />)}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AccountPage;
